import React from 'react';
import './StatCard.css';

export type TrendDirection = 'up' | 'down' | 'flat';

interface TrendProps {
  value: number;       // e.g. 6.4 (percentage points or %, caller decides suffix)
  direction: TrendDirection;
  isGood?: boolean;    // whether this direction is a positive outcome (colors green vs red)
  suffix?: string;      // e.g. "pt", "%"
}

interface StatCardProps {
  icon?: React.ReactNode;
  iconColor?: 'blue' | 'green' | 'red' | 'amber' | 'purple' | 'gray';
  label: string;
  value: React.ReactNode;
  sublabel?: string;
  tooltip?: string;    // one-liner explaining what this metric means/how it's calculated
  trend?: TrendProps;
  size?: 'primary' | 'secondary';
  onClick?: () => void;
}

const TrendArrow: React.FC<{ direction: TrendDirection }> = ({ direction }) => {
  if (direction === 'up') {
    return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 19V5M5 12l7-7 7 7"/></svg>;
  }
  if (direction === 'down') {
    return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
  }
  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 12h14"/></svg>;
};

export const StatCard: React.FC<StatCardProps> = ({
  icon, iconColor = 'blue', label, value, sublabel, tooltip, trend, size = 'primary', onClick,
}) => {
  const trendGood = trend ? (trend.direction === 'flat' ? null : (trend.isGood ?? trend.direction === 'up')) : null;

  return (
    <div
      className={`stat-card-shared stat-card-${size}${onClick ? ' stat-card-clickable' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="stat-card-top">
        {icon && <div className={`stat-card-icon stat-card-icon-${iconColor}`}>{icon}</div>}
        {trend && (
          <span className={`stat-card-trend${trendGood === null ? ' trend-neutral' : trendGood ? ' trend-good' : ' trend-bad'}`}>
            <TrendArrow direction={trend.direction} />
            {trend.value}{trend.suffix ?? ''}
          </span>
        )}
      </div>
      <div className="stat-card-label">
        {label}
        {tooltip && (
          <span className="stat-card-info" title={tooltip} aria-label={tooltip}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
          </span>
        )}
      </div>
      <div className="stat-card-value">{value}</div>
      {sublabel && <div className="stat-card-sublabel">{sublabel}</div>}
    </div>
  );
};

export default StatCard;
